import React, { createContext, useMemo } from 'react';
import { TeamMember } from '@marageti/z4-sdk/lib/people';
import { RoleName } from '@marageti/z4-sdk/lib/auth';
import { useAuthStore } from '../..';

export const FEATURES = {
  JUST_CALL_DIALER: 'JustCallDialer',
  PECV_INFERENCE_DETAILS: 'PecvInferenceDetails',
} as const;

export type FeatureName = (typeof FEATURES)[keyof typeof FEATURES];

/**
 * Role list and email list are independent checks: if both are configured, matching either one is enough.
 * Only non-empty arrays count; if neither list is set, access is denied.
 */
export type FeatureConfig = {
  allowedRoles?: string[];
  allowedUsers?: string[];
};

export type FeatureMap = Record<FeatureName, FeatureConfig>;

export const DEFAULT_FEATURE_MAP: FeatureMap = {
  [FEATURES.JUST_CALL_DIALER]: {
    allowedRoles: [RoleName.CARE],
    // allowedUsers: ['kyru@zicasso.com', 'bb@zicasso.com'],
  },
  [FEATURES.PECV_INFERENCE_DETAILS]: {
    allowedRoles: [RoleName.ADMIN],
  },
};

export type FeatureFlagContextValue = {
  teamMember: TeamMember | null;
  features: FeatureMap;
};

export const FeatureFlagContext = createContext<FeatureFlagContextValue | null>(null);

export type FeatureFlagProviderProps = {
  children: React.ReactNode;
  features?: FeatureMap;
};

export function FeatureFlagProvider({ children, features = DEFAULT_FEATURE_MAP }: FeatureFlagProviderProps) {
  const { loggedInTeamMember } = useAuthStore();

  const contextValue = useMemo(
    (): FeatureFlagContextValue => ({
      teamMember: loggedInTeamMember,
      features,
    }),
    [loggedInTeamMember, features],
  );

  return (
    <FeatureFlagContext.Provider value={contextValue}>
      {children}
    </FeatureFlagContext.Provider>
  );
}
